<template>
  <div class="rb-ob-form rb-crisis-page">
    <header>
      <div class="rb-ob-progress" :aria-label="$t('intake.progressLabel')">
        <div class="rb-ob-progress-meta">
          <span>{{ progressMeta() }}</span>
          <span>{{ $t('intake.stepLabels.crisis') }}</span>
        </div>
        <div class="rb-ob-progress-bar" aria-hidden="true">
          <i :style="{ '--p': progressPercent() }"></i>
        </div>
      </div>
      <div class="rb-crisis-heading">
        <svg class="rb-crisis-icon" viewBox="0 0 32 32" fill="none" aria-hidden="true" focusable="false">
          <path d="M16 3 27 8v8c0 6-6 11-11 13C11 27 5 22 5 16V8L16 3Z" stroke="currentColor" stroke-width="2"/>
          <path d="M16 10v7m0 5v-1" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
        </svg>
        <p class="rb-ob-privacy-note">{{ $t('intake.crisisTitle') }}</p>
        <h1 class="rb-ob-title">{{ $t('intake.crisisSafetyTitle') }}</h1>
      </div>
    </header>

    <div class="rb-ob-status-panel is-urgent">
      <template v-if="isFa">
        <p v-for="(paragraph, index) in safetyParagraphs" :key="'safety-'+index" class="rb-ob-privacy-note">{{ paragraph }}</p>
      </template>
      <template v-else>
        <p class="rb-ob-privacy-note">{{ $t('intake.crisisIntro') }}</p>
        <p class="rb-ob-privacy-note">{{ $t('intake.crisisHeading') }}</p>
        <p class="rb-ob-privacy-note">{{ $t('intake.crisisBody') }}</p>
      </template>
      <p><router-link :to="safetyPolicyLink">{{ isFa ? 'راهنمای ایمنی و منابع کمک فوری' : 'Safety guide (Persian)' }}</router-link></p>
      <p v-if="loadError" class="rb-ob-err is-visible" role="alert">{{ loadError }}</p>
      <ul v-if="contacts.length" class="rb-ob-contact-list">
        <li v-for="item in contacts" :key="item.tel + item.id">
          <a :href="'tel:' + item.tel">
            <span class="rb-contact-name">{{ contactLabel(item) }}</span>
            <span class="rb-contact-action"><bdi dir="ltr">{{ contactNumber(item.tel) }}</bdi><span>{{ $t('intake.crisisContactCall') }}</span></span>
          </a>
        </li>
      </ul>
      <div v-else-if="!loading" class="rb-ob-privacy-note" role="status">
        <p>{{ $t('intake.emergencyIfIran') }}</p>
      </div>
    </div>

    <div class="rb-ob-actions">
      <a
          v-if="primaryTel"
          class="rb-ob-submit"
          :href="'tel:' + primaryTel"
      >
        {{ $t('intake.crisisContactCall') }} <bdi dir="ltr">{{ contactNumber(primaryTel) }}</bdi>
      </a>
      <button
          v-else
          class="rb-ob-submit"
          type="button"
          :disabled="loading"
          @click="fetchContacts"
      >
        {{ $t('intake.crisisSeeHelp') }}
      </button>
      <router-link class="rb-ob-submit rb-ob-submit-secondary" :to="supportLink">{{ $t('intake.disp.accountSupportAction') }}</router-link>
      <p class="rb-ob-privacy-note">{{ $t('intake.disp.accountSupportNote') }}</p>
      <transition name="rb-ob-swap">
        <intake-emergency-box
            v-if="showHelp"
            :contacts="contacts"
            @open-crisis="onOpenCrisisFromBox"
        />
      </transition>
      <p class="rb-ob-status" role="status" aria-live="polite"></p>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {toFaDigits} from '@/intake/intakeState'
import {loadCrisisResourceResult} from '@/intake/crisisResources'
import IntakeEmergencyBox from './IntakeEmergencyBox.vue'
import {getLegalDocument} from '@/legal/documents'

export default {
  name: 'IntakeCrisisStep',
  components: {IntakeEmergencyBox},
  data() {
    return {
      showHelp: true,
      contacts: [],
      loading: true,
      loadError: '',
    }
  },
  computed: {
    isFa() { return this.$route.params.lang !== 'en' },
    safetyParagraphs() { return getLegalDocument('safety').paragraphs.slice(0, 2).map(paragraph => paragraph.text) },
    safetyPolicyLink() { return {name:'legalDocument',params:{lang:this.$route.params.lang||'fa',documentId:'safety'}} },
    supportLink() {
      return {
        name: 'portalRequestNew',
        params: {lang: this.$route.params.lang || 'fa'},
        query: {topic: 'ACCESS', from: 'intake-help', kind: 'crisis'},
      }
    },
    primaryTel() {
      return (this.contacts[0] && this.contacts[0].tel) || ''
    },
  },
  async created() {
    await this.fetchContacts()
  },
  methods: {
    contactNumber(tel) { return this.$route.params.lang === 'en' ? tel : toFaDigits(tel) },
    progressPercent() { return '33%' },
    progressMeta() {
      const digit = this.$route.params.lang === 'en' ? String : toFaDigits
      return this.$t('intake.progressMeta', {
        step: digit(1),
        total: digit(3),
      })
    },
    contactLabel(item) {
      const key = 'intake.crisisContact.' + item.id
      if (this.$te && this.$te(key)) {
        return this.$t(key)
      }
      return item.displayName || item.id
    },
    async fetchContacts() {
      this.loading = true
      this.loadError = ''
      try {
        const result = await loadCrisisResourceResult('IR')
        this.contacts = result.contacts || []
        this.showHelp = this.contacts.length > 0
        if (!this.contacts.length) {
          this.loadError = this.$t('intake.emergencyIfIran')
        }
      } catch (e) {
        this.contacts = []
        this.loadError = this.$t('intake.emergencyIfIran')
      } finally {
        this.loading = false
      }
    },
    onOpenCrisisFromBox() {
      this.showHelp = true
      this.fetchContacts()
    },
  },
}
</script>
